import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Download, Monitor, Users, BarChart2, Calendar, 
  Play, Home, Newspaper, Mic, Image, ExternalLink,
  Maximize2, Trash2
} from "lucide-react";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Demo feature data
const DEMO_FEATURES = [
  {
    id: "homepage",
    title: "Home Page",
    description: "Main landing page with hero section, upcoming games, news, and sponsor ticker",
    icon: Home,
    color: "#F97316",
    previewPath: "/",
    sections: ["Hero Banner", "Next Event Countdown", "Upcoming Games", "Top Stories", "Sponsor Ticker"]
  },
  {
    id: "watch-party",
    title: "Watch Party",
    description: "Synchronized viewing experience where fans can watch games together in real-time",
    icon: Users,
    color: "#8B5CF6",
    previewPath: "/watch-party",
    sections: ["Synced Video Player", "Viewer List", "Party Controls"]
  },
  {
    id: "brackets",
    title: "Tournament Brackets",
    description: "Interactive bracket system for tournaments with single and double elimination support",
    icon: BarChart2,
    color: "#10B981",
    previewPath: "/events",
    sections: ["Single Elimination", "Double Elimination", "Team Seeding", "Live Score Updates"]
  },
  {
    id: "schedule",
    title: "Broadcast Schedule",
    description: "Complete schedule of all upcoming and past broadcasts with filtering options",
    icon: Calendar,
    color: "#3B82F6",
    previewPath: "/schedule",
    sections: ["Calendar View", "Sport Filters", "Date Navigation", "Game Details"]
  },
  {
    id: "video-player",
    title: "Video Player with Stats",
    description: "YouTube-embedded player with live stats overlay and team information",
    icon: Play,
    color: "#EF4444",
    previewPath: "/game",
    sections: ["YouTube Integration", "Stats Panel", "Matchup Widget", "Score Display"]
  },
  {
    id: "news",
    title: "News",
    description: "Sports news and articles section with featured stories and categories",
    icon: Newspaper,
    color: "#6366F1",
    previewPath: "/news",
    sections: ["Featured Articles", "Category Filtering", "Writer Profiles", "Related Stories"]
  },
  {
    id: "podcasts",
    title: "Podcasts",
    description: "Audio content section for sports podcasts and show episodes",
    icon: Mic,
    color: "#A855F7",
    previewPath: "/podcasts",
    sections: ["Episode List", "Audio Player", "Show Information", "Subscribe Links"]
  }
];

// Demo Card Component
const DemoCard = ({ demo, onPreview, onDownload }) => {
  const IconComponent = demo.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900 border border-white/10 overflow-hidden group hover:border-white/20 transition-colors"
    >
      {/* Preview Area */}
      <div 
        className="aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 relative flex items-center justify-center cursor-pointer"
        onClick={() => onPreview(demo)}
        style={{ 
          background: `linear-gradient(135deg, ${demo.color}15 0%, transparent 50%), linear-gradient(to bottom, #18181b, #0a0a0a)` 
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <IconComponent 
            className="w-20 h-20 opacity-20" 
            style={{ color: demo.color }} 
          />
        </div>
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
          <Button
            onClick={(e) => { e.stopPropagation(); onPreview(demo); }}
            className="bg-white text-black hover:bg-zinc-200 rounded-none px-4 py-2 text-sm font-bold flex items-center gap-2"
          >
            <Maximize2 className="w-4 h-4" />
            Preview
          </Button>
          <Button
            onClick={(e) => { e.stopPropagation(); onDownload(demo); }}
            className="bg-orange-500 text-black hover:bg-orange-400 rounded-none px-4 py-2 text-sm font-bold flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
        </div>
        
        {/* Feature Badge */}
        <div 
          className="absolute top-3 left-3 px-3 py-1 text-xs font-bold uppercase tracking-wider"
          style={{ backgroundColor: demo.color, color: '#000' }}
        >
          {demo.title}
        </div>
      </div>
      
      {/* Info */}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="w-10 h-10 flex items-center justify-center"
            style={{ backgroundColor: `${demo.color}20` }}
          >
            <IconComponent className="w-5 h-5" style={{ color: demo.color }} />
          </div>
          <h3 className="text-lg font-bold text-white">{demo.title}</h3>
        </div>
        
        <p className="text-sm text-zinc-400 mb-4 leading-relaxed">{demo.description}</p>
        
        {/* Feature Sections */}
        <div className="flex flex-wrap gap-2">
          {demo.sections.map((section, idx) => (
            <span 
              key={idx}
              className="px-2 py-1 bg-zinc-800 text-zinc-400 text-[11px] uppercase tracking-wider"
            >
              {section}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Preview Modal
const PreviewModal = ({ demo, onClose }) => {
  if (!demo) return null;
  
  const IconComponent = demo.icon;
  const previewUrl = `${window.location.origin}${demo.previewPath}`;
  
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8">
      <div className="max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 flex items-center justify-center"
              style={{ backgroundColor: demo.color }}
            >
              <IconComponent className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{demo.title}</h2>
              <p className="text-sm text-zinc-400">{demo.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white hover:bg-zinc-700 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-zinc-800 text-white hover:bg-zinc-700 text-sm"
            >
              Close
            </button>
          </div>
        </div>
        
        {/* Preview iframe */}
        <div className="flex-1 bg-zinc-900 border border-white/10 overflow-hidden">
          <iframe
            src={previewUrl}
            className="w-full h-full min-h-[600px]"
            title={`${demo.title} Preview`}
          />
        </div>
      </div>
    </div>
  );
};

export const DemosTab = () => {
  const [previewDemo, setPreviewDemo] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const handlePreview = (demo) => {
    setPreviewDemo(demo);
  };

  const handleDownload = async (demo) => {
    setDownloading(demo.id);
    
    // Open the page in a new window for manual screenshot
    // In production, you could use a server-side screenshot service
    const previewUrl = `${window.location.origin}${demo.previewPath}`;
    
    // Create a notification about how to download
    const instructions = `
To download a high-resolution demo of "${demo.title}":

1. A new tab will open with the ${demo.title} page
2. Press F12 to open Developer Tools
3. Click the "Device Toolbar" icon (or press Ctrl+Shift+M)
4. Set resolution to 1920x1080 or higher
5. Right-click the page and select "Capture screenshot" or use browser's screenshot tool
6. Save the image

Alternatively, use your system's screenshot tool (Win+Shift+S on Windows, Cmd+Shift+4 on Mac)
    `;
    
    // Open the preview page
    window.open(previewUrl, '_blank');
    
    // Show instructions
    alert(instructions);
    
    setDownloading(null);
  };

  const handleDownloadAll = () => {
    const instructions = `
To download all demo pages:

Each feature page will open in a new tab. Use your browser's screenshot tool or the Print to PDF feature to save high-resolution versions.

Recommended settings:
- Resolution: 1920x1080 or higher
- Format: PNG for best quality
- Include: Full page capture

Opening all demo pages now...
    `;
    
    alert(instructions);
    
    // Open each demo page with a slight delay
    DEMO_FEATURES.forEach((demo, index) => {
      setTimeout(() => {
        window.open(`${window.location.origin}${demo.previewPath}`, '_blank');
      }, index * 500);
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Monitor className="w-7 h-7 text-orange-500" />
            Feature Demos
          </h2>
          <p className="text-zinc-400 mt-1">
            High-resolution previews of all site features for presentations and marketing
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={handleDownloadAll}
            className="bg-orange-500 text-black hover:bg-orange-400 rounded-none px-6 py-3 font-bold flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Download All Demos
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-zinc-800/50 border border-orange-500/30 p-4 flex items-start gap-4">
        <Image className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-white font-bold mb-1">High-Resolution Demo Pages</h3>
          <p className="text-sm text-zinc-400">
            Click on any feature card to preview it, or click Download to open the page for screenshot capture. 
            For best results, use 1920x1080 or higher resolution. These demos are perfect for investor presentations, 
            sponsor pitches, and marketing materials.
          </p>
        </div>
      </div>

      {/* Demo Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {DEMO_FEATURES.map((demo) => (
          <DemoCard
            key={demo.id}
            demo={demo}
            onPreview={handlePreview}
            onDownload={handleDownload}
          />
        ))}
      </div>

      {/* Usage Tips */}
      <div className="bg-zinc-900 border border-white/10 p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Monitor className="w-5 h-5 text-orange-500" />
          Tips for Best Results
        </h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="text-white font-bold mb-2">Browser Screenshot</h4>
            <p className="text-zinc-400">
              Use Chrome DevTools (F12 → Device Toolbar → Set custom resolution → Right-click → Capture screenshot)
            </p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-2">Full Page Capture</h4>
            <p className="text-zinc-400">
              For full-page screenshots, use extensions like GoFullPage or Firefox built-in screenshot tool
            </p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-2">Recommended Size</h4>
            <p className="text-zinc-400">
              1920x1080 for standard presentations, 2560x1440 or 3840x2160 for high-DPI displays
            </p>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewDemo && (
        <PreviewModal 
          demo={previewDemo} 
          onClose={() => setPreviewDemo(null)} 
        />
      )}
    </motion.div>
  );
};

export default DemosTab;
